import React, { useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './DefaultsConfigWizard.scss';
import { DefaultConfigValue, DefaultConfigWizardContext, DefaultConfigWizardContextValue } from "./DefaultsConfigWizardContext";
import { TabWizard, TabWizardRef } from "@byzzer/ui-components";
import useState from 'react-usestateref';
import { useUser } from "@/contexts/UserContext";
import { MarketStep, ProductStep, TimePeriodStep, DemographicsStep } from "../Steps";
import { DefaultRunConfig, RunConfigDefaults } from "@/types/ApiTypes";
import { useMarketService } from "@/services/market.service";
import { FocusKpiStep } from "../Steps/FocusKpiStep";
import { RunConfigOptions } from "@/types/RunConfigOptions";
import { DefaultConfigLabels } from "@/pages/Onboarding/OnboardingSteps";
import { OnboardingStepCode } from "@/pages/Onboarding/OnboardingWizard";
import { ReportDatatype } from "@/types/ReportRun";
import { normalizeOshDemographics } from "@/utils/normalizeOshDemographics";

export type DefaultsConfigWizardProps = {
    onComplete?: (defaultConfig: DefaultRunConfig) => void;
    onCancel?: () => void;
    data?: any;
    busy?: boolean;
    busyCompleteButtonText?: string;
    busyCompleteButtonTip?: ReactNode;
    defaultPreset?: Partial<RunConfigDefaults>;
    defaultConfigOptions:  RunConfigOptions[];
    labelsAndTips: DefaultConfigLabels;
    code?: OnboardingStepCode;
    selectedDataset: ReportDatatype;
}
const OMNI_DISCLAIMER = 'Note: Default selections do not apply to Omnisales data and reports.'

type ConfigOptionsType = 'product' | 'market' | 'time_period' | 'focuskpi' | 'demographics'; // Todo- DRY this up

export type StepRef = any;

const FILTER_BY_OPTION_TYPE: Record<ConfigOptionsType, any> = {
    product: ProductStep,
    market: MarketStep,
    time_period: TimePeriodStep,
    focuskpi: FocusKpiStep,
    demographics: DemographicsStep,
};

const DATASET_DISPLAY_NAME: Record<ReportDatatype, string> = {
    rms: 'RMS',
    cps: 'CPS',
    omni: 'Omnisales',
    osh: 'Omnishopper',
};


export const DefaultsConfigWizard = forwardRef(({
    onComplete,
    onCancel,
    busy,
    busyCompleteButtonText,
    busyCompleteButtonTip,
    defaultPreset,
    defaultConfigOptions,
    labelsAndTips,
    code,
    selectedDataset,
    ...props
}: DefaultsConfigWizardProps, ref) => {

    const baseClassName = 'byz-defaults-config-wizard';

    const {
        defaultRunConfig,
    } = useUser();
    const {getEnabledMarkets} = useMarketService();
  

    useEffect(() => {         
        wizardRef.current!.activeStepIndex = 0;
     },[]);

    // Get the config for the currently selected dataset
    const currentDatasetConfig = defaultRunConfig?.[selectedDataset || 'rms'] ?? {};

    const {
        brands = [],
        timePeriod,
        markets,
        categories = [],
        subcategories = [],
        characteristics = [],
        focusKPI,
        productDimensions = [],
        subMarkets = [],
        omniProductLevel,
        omniFocusProducts,
        demographics = [],
        demographicDimensions = [],
    } = currentDatasetConfig;

    const normalizedDemographics = useMemo(
        () => (selectedDataset === 'osh' ? normalizeOshDemographics(demographics as any[]) : demographics),
        [selectedDataset, demographics]
    );

    const initialValues = useMemo(() => ({
        brands,
        categories,
        subcategories,
        characteristics,
        markets: getEnabledMarkets(markets, categories, false, false, undefined, undefined, false),
        timePeriod,
        focusKPI,
        productDimensions,
        subMarkets,
        omniProductLevel,
        omniFocusProducts,
        demographics: normalizedDemographics,
        demographicDimensions,
        ...defaultPreset,
    }), []); // Only compute once on mount

    const [contextValue, setContextValue, contextValueRef] = useState<DefaultConfigWizardContextValue>({
        value: { ...initialValues },
        onChange(name: keyof DefaultConfigValue, value: any): void {
            const shouldResetMarkets = name === 'categories';

            setContextValue((current) => ({
                ...current,
                value: {
                    ...current.value,
                    markets: shouldResetMarkets ? [] : current.value.markets,
                    [name]: value,
                },
            }));
        },
        selectedDataset,
        hasChanges: false,
    });

    // Compute hasChanges by comparing current values with initial values
    useEffect(() => {
        const currentValue = contextValueRef.current.value;
        const changed = JSON.stringify(currentValue) !== JSON.stringify(initialValues);
        setContextValue((current) => ({
            ...current,
            hasChanges: changed,
        }));
    }, [contextValue.value]);

    useImperativeHandle(ref, () => contextValueRef.current.value, [contextValue.value]);

    const handleComplete = useCallback(async () => {
        const value = contextValueRef.current.value;
        if (!value) return;

        const newDefaults: DefaultRunConfig = {
            ...defaultRunConfig,
            [selectedDataset]: value,
        };

        onComplete?.(newDefaults);

    }, [contextValue, selectedDataset]);

    const Wizard = TabWizard;
    const wizardRef = useRef<TabWizardRef | null>(null);

    function handleCancel() {
        onCancel?.();
    }

    return (
        <>
            <header className={classnames(`${baseClassName}__header`)}>
                <div className={classnames(`${baseClassName}__heading`)}>
                    <p className={classnames(`${baseClassName}__title`)}>
                        {DATASET_DISPLAY_NAME[selectedDataset]} Default Selections
                    </p>
                </div>
            </header>
            <DefaultConfigWizardContext.Provider value={contextValue}>
                <Wizard 
                    className={classnames(baseClassName) } 
                    ref={wizardRef}
                >
                    {defaultConfigOptions?.map((options, i) => {
                        const Step = FILTER_BY_OPTION_TYPE[options.type];
                        return (
                            <Step
                                key={`${options.type}${i}`}
                                onComplete={handleComplete}
                                onCancel={handleCancel}
                                disableSave={!contextValue.hasChanges}
                                saveDisabledTip={'Please make any change in existing config to modify the defaults.'}
                                {...{code, ...options}}
                                labelsAndTips={labelsAndTips}
                            />
                        );
                    })}
                </Wizard>
            </DefaultConfigWizardContext.Provider>
        </>
    );

});

export default DefaultsConfigWizard;

DefaultsConfigWizard.displayName = 'DefaultsConfigWizard';
